import { useEffect, useState } from 'react'
import { Skeleton } from '@mui/material'
import styles from './TicketQrCode.module.scss'

type Props = Readonly<{ value: string; label: string }>

/**
 * Renders entirely in the browser. The ticket payload is never sent to a QR
 * service, stored as an image, or written into the offline operational store.
 */
export function TicketQrCode({ value, label }: Props) {
  return <TicketQrCodeRenderer key={value} value={value} label={label} />
}

function TicketQrCodeRenderer({ value, label }: Props) {
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let active = true
    void import('qrcode')
      .then(({ toDataURL }) => toDataURL(value, { errorCorrectionLevel: 'M', margin: 1, width: 240 }))
      .then((next) => { if (active) setImageUrl(next) })
      .catch(() => { if (active) setFailed(true) })
    return () => { active = false }
  }, [value])

  if (failed) return <p className={styles.error} role="status">QR code unavailable. Use the ticket link instead.</p>
  if (!imageUrl) return <Skeleton className={styles.loading} variant="rounded" aria-label="Generating ticket QR code" />
  return <img className={styles.image} src={imageUrl} alt={`QR code for ${label}`} />
}
